import { useEffect, useState } from 'react';
import { useReintegro } from './hooks/useReintegro';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AfiliadoSelect from '../Afiliados/AfiliadoSelect';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete,
  Save as SaveIcon,
} from '@mui/icons-material';
import FileUpload from '../Common/FileUpload';
import ButtonBack from '../Breadcrumb/ButtonBack';
import { TipoCoberturaReintegroSelector } from '../Common/TipoCoberturaReintegroSelector';

const MAX_FILE_TO_UPLOAD = 3;
const MAX_FILE_SIZE_MB = 4;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

export const AddReintegro = () => {
  const history = useHistory();
  const { saveReintegro, loading, getTiposDocumentacionByCobertura, tiposDocumentacion } =
    useReintegro();
  const [disabledAddFile, setDisabledAddFile] = useState(true);
  const [extraDataArchivos, setExtraDataArchivos] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    setValue,
    getValues,
    setError,
    resetField,
    control,
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      idAfiliado: '',
      observacion: '',
      adjuntos: [],
      tipoCobertura: null,
    },
    mode: 'onTouched',
  });

  const {
    append: appendArchivo,
    remove: removeArchivo,
    fields: fieldsArchivo,
  } = useFieldArray({
    control,
    name: 'adjuntos',
  });

  // Efecto para actualizar extraDataArchivos cada vez que fieldsArchivo cambia
  useEffect(() => {
    const newExtraData = fieldsArchivo.reduce((acc, item) => {
      const id = item.tipoArchivo.idDocumentacion;
      const dimension = item.archivo.dimension;
      const esRequerido = item.tipoArchivo.requerido;

      if (!acc[id]) {
        acc[id] = {
          cantidadTotalArchivo: 0,
          dimensionTotalArchivo: 0,
          cantidadTotalRequeridos: 0,
          nombreDocumentacion: item.tipoArchivo.documentacion,
        };
      }

      acc[id].cantidadTotalArchivo += 1;
      acc[id].dimensionTotalArchivo += dimension;
      if (esRequerido) {
        acc[id].cantidadTotalRequeridos += 1;
      }

      return acc;
    }, {});

    setExtraDataArchivos(newExtraData);
  }, [fieldsArchivo]);

  const onHandleSaveReintegro = async (data) => {
    await saveReintegro(data)
      .then((response) => {
        try {
          history.push('/reintegros/ver-reintegro', {
            from: history.location,
            reintegro: { id: response?.solicitudCobertura?.idSolicitudCobertura },
          });
        } catch (e) {
          console.log(e);
        }
      })
      .catch((err) => {
        console.log('Error saving reintegro:', err);
      });
  };

  const onHandleChangeTipoCobertura = (tipoCobertura) => {
    if (tiposDocumentacion) {
      // 1. Guardamos los dirtyFields actuales de idAfiliado y observacion
      const dirty = {
        idAfiliado: dirtyFields.idAfiliado,
        observacion: dirtyFields.observacion,
      };

      // 2. Obtenemos los valores actuales para resetear con ellos
      const values = watch();

      // 3. Hacemos reset normal
      reset();
      // Esto limpia los dirtyFields

      // 4. Volvemos a marcar como dirty los mismos campos
      Object.keys(dirty).forEach((key) => {
        if (dirty[key]) {
          setValue(key, values[key], { shouldDirty: true });
        }
      });
    }
  };

  return (
    <div className='container'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ButtonBack marginBottom='1' variant='text' size='small' />
          <Card sx={{ width: '100%', p: 1, paddingX: { xs: 0, sm: 1 } }}>
            <form onSubmit={handleSubmit((data) => onHandleSaveReintegro(data))} noValidate>
              <CardContent>
                <Typography gutterBottom variant='h5' component='h2'>
                  Nuevo Reintegro
                </Typography>

                <FormControl
                  fullWidth={true}
                  variant='outlined'
                  margin='normal'
                  error={!!errors.idAfiliado}
                  required
                >
                  <AfiliadoSelect
                    size='small'
                    ref={register('idAfiliado', {
                      validate: (value) => !!value,
                    })}
                    onSelectAfiliado={(afiliado) =>
                      setValue('idAfiliado', afiliado?.afiliado?.idAfiliado || '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    onDefaultAfiliado={getValues('idAfiliado')}
                  />
                  {errors.idAfiliado && (
                    <FormHelperText sx={{ ml: 0 }}>Debe seleccionar el afiliado</FormHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <TipoCoberturaReintegroSelector
                    {...register('tipoCobertura', {
                      required: 'Debe seleccionar un tipo de reintegro',
                    })}
                    label={'Tipo de reintegro'}
                    onChangeOption={(e) => {
                      removeArchivo();
                      onHandleChangeTipoCobertura(e);
                      setValue('tipoCobertura', e, { shouldValidate: true });
                      getTiposDocumentacionByCobertura(e?.idTipoCobertura || null);
                    }}
                    error={!!(errors?.tipoCobertura || null)}
                    required
                  />
                </FormControl>

                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <TextField
                    {...register('observacion', {
                      required: 'El motivo del reintegro es un campo obligatorio',
                      minLength: { value: 10, message: 'Mínimo 10 caracteres' },
                      maxLength: { value: 500, message: 'Máximo 500 caracteres' },
                    })}
                    error={!!errors.observacion}
                    helperText={errors.observacion ? errors.observacion.message : ''}
                    label='Motivo del reintegro'
                    multiline
                    minRows={3}
                    variant='standard'
                    required
                  />
                </FormControl>

                {tiposDocumentacion &&
                  tiposDocumentacion.length > 0 &&
                  tiposDocumentacion.map((tipo) => (
                    <FormControl
                      key={tipo.idDocumentacion}
                      fullWidth={true}
                      margin='normal'
                      variant='standard'
                      error={!!errors[`idDocumentacionCount-${tipo?.idDocumentacion}`]}
                      required={!!tipo?.requerido}
                    >
                      <input
                        type='hidden'
                        {...register(`idDocumentacionCount-${tipo.idDocumentacion}`, {
                          validate: {
                            required: (value) => {
                              const esRequerido = tipo.requerido;
                              if (esRequerido) {
                                const extraData = extraDataArchivos[tipo.idDocumentacion];
                                if (!extraData || extraData.cantidadTotalRequeridos < 1) {
                                  return 'Debe adjuntar al menos un archivo de este tipo';
                                }
                              }
                            },
                            maxFileSize: (value) => {
                              const extraData = extraDataArchivos[tipo.idDocumentacion];
                              if (
                                extraData &&
                                extraData.dimensionTotalArchivo > MAX_FILE_SIZE_MB * 1024 * 1024
                              ) {
                                return `El tamaño total de archivos excede el límite de ${MAX_FILE_SIZE_MB}MB`;
                              }

                              return true;
                            },
                          },
                        })}
                      />
                      <InputLabel htmlFor={`idDocumentacion-${tipo.idDocumentacion}`} shrink>
                        Adjuntar documentación de tipo <b>{tipo.descripcion}</b>
                      </InputLabel>
                      <Box
                        id={`idDocumentacion-${tipo.idDocumentacion}`}
                        sx={{
                          m: 0,
                          mt: 3,
                          px: 2,
                          py: 1,
                          borderRadius: { xs: 0, sm: '4px' },
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          borderLeftWidth: { xs: 0, sm: 1 },
                          borderRightWidth: { xs: 0, sm: 1 },
                          overflow: 'hidden',
                          background: 'rgb(241 241 241 / 35%)',
                        }}
                      >
                        <Grid
                          container
                          justifyContent={'space-between'}
                          spacing={2}
                          alignItems={'center'}
                        >
                          <Grid item xs={12}>
                            <Alert severity='info' icon={false}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: 2,
                                  rowGap: 0,
                                  alignItems: { xs: 'flex-start', sm: 'center' },
                                }}
                              >
                                <Typography variant='body2' sx={{ color: 'info.dark' }}>
                                  <strong>Cantidad de archivos:</strong>{' '}
                                  {extraDataArchivos[tipo.idDocumentacion]?.cantidadTotalArchivo ||
                                    0}{' '}
                                  / {MAX_FILE_TO_UPLOAD}
                                </Typography>
                                <Typography variant='body2' sx={{ color: 'info.dark' }}>
                                  <strong>Tamaño de archivos:</strong>{' '}
                                  {extraDataArchivos[tipo.idDocumentacion]?.dimensionTotalArchivo
                                    ? (
                                        extraDataArchivos[tipo.idDocumentacion]
                                          .dimensionTotalArchivo /
                                        1024 /
                                        1024
                                      ).toFixed(2)
                                    : '0.00'}
                                  MB / {MAX_FILE_SIZE_MB}MB
                                </Typography>
                              </Box>
                            </Alert>
                          </Grid>

                          <Grid item lg={8} md={8} sm={8} xs={12}>
                            <FormControl
                              fullWidth={true}
                              margin='none'
                              error={!!errors[`idDocumentacion-${tipo.idDocumentacion}`]}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 2,
                                  rowGap: 1,
                                  alignItems: 'center',
                                }}
                              >
                                <Controller
                                  control={control}
                                  name={`idDocumentacion-${tipo.idDocumentacion}`}
                                  render={({ field }) => {
                                    return (
                                      <FileUpload
                                        {...field}
                                        id='archivoUpload'
                                        component='IconButton'
                                        accept={[...ALLOWED_TYPES]}
                                        compressFile
                                        onChange={(value) => {
                                          field.onChange(value);
                                          trigger(`idDocumentacion-${tipo.idDocumentacion}`);
                                          if (value) {
                                            setDisabledAddFile(false);
                                          }
                                        }}
                                        color='neutral'
                                        startIcon={<CloudUploadIcon />}
                                        size='small'
                                        variant='contained'
                                        aria-label='Subir archivo'
                                        disabled={
                                          extraDataArchivos[tipo.idDocumentacion]
                                            ?.cantidadTotalArchivo === MAX_FILE_TO_UPLOAD
                                        }
                                      >
                                        Subir archivo
                                      </FileUpload>
                                    );
                                  }}
                                />
                                {fieldsArchivo.length !== MAX_FILE_TO_UPLOAD && (
                                  <Typography variant='' fontSize={12}>
                                    {getValues(`idDocumentacion-${tipo.idDocumentacion}`)
                                      ?.fileName || 'Ningún archivo seleccionado...'}
                                  </Typography>
                                )}
                                {errors[`idDocumentacion-${tipo.idDocumentacion}`] && (
                                  // <p style={{ color: 'red', marginTop: '5px' }}>{errors.archivo.message}</p>
                                  <FormHelperText sx={{ marginLeft: '0px', marginTop: '8px' }}>
                                    {errors[`idDocumentacion-${tipo.idDocumentacion}`].message}
                                  </FormHelperText>
                                )}
                              </Box>
                            </FormControl>
                          </Grid>

                          <Grid item lg={4} md={4} sm={4} xs={12}>
                            <FormControl fullWidth={true} margin='none'>
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  justifyContent: 'flex-end',
                                }}
                              >
                                <Button
                                  variant='contained'
                                  color='primary'
                                  size='small'
                                  startIcon={<AddIcon />}
                                  disabled={(() => {
                                    const tipoCobertura = getValues('tipoCobertura');
                                    const idDocumentacion = tipo.idDocumentacion;

                                    const result = !!(
                                      disabledAddFile ||
                                      !tipoCobertura ||
                                      !getValues(`idDocumentacion-${idDocumentacion}`) ||
                                      (extraDataArchivos[idDocumentacion]?.cantidadTotalArchivo ??
                                        0) >= MAX_FILE_TO_UPLOAD
                                    );

                                    return result;
                                  })()}
                                  onClick={() => {
                                    const archivoValue = getValues(
                                      `idDocumentacion-${tipo.idDocumentacion}`,
                                    );
                                    if (!archivoValue) {
                                      setError(`idDocumentacion-${tipo.idDocumentacion}`, {
                                        type: 'validate',
                                        message: 'Debe adjuntar un archivo',
                                      });
                                    }
                                    // controlar que archivoValue ya no se encuentre cargado de archivos
                                    const archivoYaCargado = fieldsArchivo.some(
                                      (item) =>
                                        item?.tipoArchivo?.idDocumentacion ===
                                          tipo.idDocumentacion &&
                                        item?.archivo?.fileName === archivoValue?.fileName,
                                    );
                                    if (archivoYaCargado) {
                                      setError(`idDocumentacion-${tipo.idDocumentacion}`, {
                                        type: 'validate',
                                        message:
                                          'El archivo que intenta adjuntar ya fue agregado recientemente',
                                      });
                                    }
                                    if (errors[`idDocumentacion-${tipo.idDocumentacion}`]) {
                                      resetField(`idDocumentacion-${tipo.idDocumentacion}`);
                                      return;
                                    }
                                    appendArchivo({
                                      tipoArchivo: { ...tipo },
                                      archivo: archivoValue,
                                    });
                                    setValue(`idDocumentacion-${tipo.idDocumentacion}`, null, {
                                      shouldDirty: false,
                                    });
                                    resetField(`idDocumentacion-${tipo.idDocumentacion}`);

                                    const counttingFieldsTipo = fieldsArchivo.filter(
                                      (item) =>
                                        item?.tipoArchivo?.idDocumentacion === tipo.idDocumentacion,
                                    ).length;

                                    setValue(
                                      `idDocumentacionCount-${tipo.idDocumentacion}`,
                                      counttingFieldsTipo + 1,
                                      { shouldValidate: false, shouldDirty: false },
                                    );
                                  }}
                                >
                                  Agregar
                                </Button>
                              </Box>
                            </FormControl>
                          </Grid>

                          {fieldsArchivo?.length > 0 &&
                            extraDataArchivos[tipo.idDocumentacion]?.cantidadTotalArchivo > 0 && (
                              <Grid item xs={12} mt={1}>
                                <TableContainer>
                                  <Table
                                    sx={{ minWidth: 200, width: '100%' }}
                                    size='small'
                                    aria-label='a dense table'
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell
                                          align='center'
                                          sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                                        >
                                          #
                                        </TableCell>
                                        <TableCell>Tipo de archivo</TableCell>
                                        <TableCell>Nombre de archivo</TableCell>
                                        <TableCell align='right'></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {fieldsArchivo.map((row, index) => {
                                        if (
                                          row.tipoArchivo.idDocumentacion === tipo.idDocumentacion
                                        ) {
                                          return (
                                            <TableRow
                                              key={row.id}
                                              sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                              }}
                                            >
                                              <TableCell
                                                align='center'
                                                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                                              >
                                                {index + 1}
                                              </TableCell>
                                              <TableCell
                                                component='th'
                                                scope='row'
                                                sx={{ paddingX: { xs: '0px', sm: '16px' } }}
                                              >
                                                <Typography
                                                  variant='span'
                                                  sx={{ display: { xs: 'inline', sm: 'none' } }}
                                                >
                                                  {`#${index + 1} - `}
                                                </Typography>
                                                {row?.tipoArchivo?.descripcion}{' '}
                                                <Grid sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                  <b>{row?.archivo?.fileName}</b>
                                                </Grid>
                                              </TableCell>
                                              <TableCell
                                                align='left'
                                                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                                              >
                                                <b>{row?.archivo?.fileName}</b>
                                              </TableCell>
                                              <TableCell
                                                align='right'
                                                sx={{ paddingRight: { xs: '0px', sm: '16px' } }}
                                              >
                                                <Button
                                                  sx={{
                                                    display: { xs: 'inline-flex', sm: 'none' },
                                                    minWidth: '24px',
                                                    padding: '10px',
                                                    '& span': { margin: 0 },
                                                  }}
                                                  aria-label='Eliminar archivo.'
                                                  variant='contained'
                                                  color='neutral'
                                                  size='small'
                                                  startIcon={<Delete />}
                                                  onClick={() => {
                                                    removeArchivo(index);
                                                  }}
                                                ></Button>
                                                <Button
                                                  sx={{
                                                    display: { xs: 'none', sm: 'inline-flex' },
                                                  }}
                                                  aria-label='Eliminar archivo.'
                                                  variant='contained'
                                                  color='neutral'
                                                  size='small'
                                                  startIcon={<Delete />}
                                                  onClick={() => {
                                                    removeArchivo(index);
                                                  }}
                                                >
                                                  Eliminar
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                        return null;
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Grid>
                            )}
                        </Grid>
                      </Box>

                      {errors[`idDocumentacionCount-${tipo.idDocumentacion}`] && (
                        <FormHelperText>
                          {errors[`idDocumentacionCount-${tipo.idDocumentacion}`].message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ))}
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end', mx: 1, mt: 2 }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'flex-end',
                  }}
                >
                  <LoadingButton
                    startIcon={<SaveIcon />}
                    variant='contained'
                    color='primary'
                    type='submit'
                    loading={loading}
                    disabled={false}
                  >
                    Guardar
                  </LoadingButton>
                </Box>
              </CardActions>
            </form>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};
